<template>
  <v-container fluid>
    <v-card>
      <v-toolbar flat dense color="primary" dark>
        <v-toolbar-title>Edit Quotation</v-toolbar-title>

        <v-spacer></v-spacer>

        <!-- PRINT BUTTON -->
        <v-btn elevation="0" color="success" class="mr-2" @click="printPdf">
          <v-icon left>mdi-printer</v-icon>
          Print
        </v-btn>

        <!-- LOCK BUTTON -->
        <v-btn elevation="0" icon :color="form.isLocked ? 'green' : 'red'" class="ml-2"
          @click="form.isLocked ? confirmUnlock() : confirmLock()">
          <v-icon>
            {{ form.isLocked ? 'mdi-lock-open' : 'mdi-lock' }}
          </v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <v-form>

          <!-- BASIC INFO -->
          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field hide-details="auto" label="Subject" v-model="form.data.subject" outlined dense
                :error-messages="form.errors?.subject" />
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete hide-details="auto" item-value="id" item-text="code" label="Project Code"
                :items="computedProjectCodes" outlined dense no-filter :search-input.sync="projectCodeSearch" clearable
                @change="onProjectCodeChange" v-model="form.data.project_code_id"
                :error-messages="form.errors?.project_code_id">
                <template v-slot:item="{ item }">
                  (<small v-html="highlightText(item.code, projectCodeSearch)"></small>) <small>&nbsp;-&nbsp;</small>
                  <span v-html="highlightText(item.description, projectCodeSearch)"></span>
                </template>
              </v-autocomplete>
            </v-col>

            <v-col cols="12" md="6">
              <v-autocomplete hide-details="auto" item-value="id" item-text="name" label="Customer" :items="customers"
                no-filter :search-input.sync="customerSearch" :error-messages="form.errors?.customer_id" outlined dense
                clearable v-model="form.data.customer_id">
                <template #item="{ item }">
                  <small v-html="`(${highlightText(item.id, customerSearch)})`">
                    ({! item.id !})
                  </small>&nbsp;-&nbsp;
                  <span v-html="highlightText(item.name, customerSearch)"></span>
                </template>
              </v-autocomplete>
            </v-col>

            <v-col cols="12" md="6">
              <v-select hide-details="auto" label="VAT Type" :items="vatTypes" v-model="form.data.vat_type" outlined
                dense :error-messages="errors?.vat_type" />
            </v-col>


            <v-col cols="12" md="6">
              <v-text-field hide-details="auto" label="Actual Date" type="date" v-model="form.data.actual_date" outlined
                dense :error-messages="form.errors?.actual_date" />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field hide-details="auto" label="Discount Rate" type="number" v-model="form.data.discount_rate"
                outlined dense :error-messages="form.errors?.discount_rate" />
            </v-col>

            <v-col cols="12" md="12">
              <v-textarea hide-details="auto" label="Notes" v-model="form.data.notes" outlined dense
                :error-messages="form.errors?.notes" />
            </v-col>
          </v-row>
          <v-divider class="my-4" />

          <v-row dense>
            <v-col cols="12" md="6">
              <v-combobox hide-details="auto" v-model="selectedTac" :items="tacOptions" :loading="tacLoading"
                item-text="alias" item-value="id" label="Search / Select T&C Preset" outlined dense clearable
                return-object @change="applyTac" />
            </v-col>

            <v-col cols="12" md="6" class="d-flex align-center">
              <v-btn color="primary" small class="mr-2" @click="saveTac">
                <v-icon left>mdi-content-save</v-icon>
                Save as Preset
              </v-btn>

              <v-btn color="warning" small :disabled="!selectedTac || !selectedTac.id" @click="updateTac">
                <v-icon left>mdi-pencil</v-icon>
                Update Preset
              </v-btn>
            </v-col>
          </v-row>
          <!-- TERMS & CONDITIONS -->
          <v-divider class="my-4" />

          <strong>Terms & Conditions</strong>

          <v-row dense>
            <v-col cols="12" v-for="(tc, i) in form.data.terms_and_conditions" :key="i">
              <v-card outlined class="pa-3">
                <v-row dense align="center">
                  <v-col cols="12" md="5">
                    <v-text-field hide-details="auto" label="Key" v-model="tc.key" outlined dense />
                  </v-col>

                  <v-col cols="12" md="5">
                    <v-text-field hide-details="auto" label="Value" v-model="tc.value" outlined dense
                      :error-messages="form.errors?.[`terms_and_conditions.${tc.key}`]" />
                  </v-col>

                  <v-col cols="12" md="2" class="text-right">
                    <v-btn color="red" text @click="removeTC(i)">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>

          <v-btn small class="mt-2" @click="addTC">
            <v-icon left>mdi-plus</v-icon>
            Add Term
          </v-btn>

          <!-- ITEMS -->
          <v-divider class="my-4" />

          <strong>Items</strong>

          <v-data-table hide-default-footer :headers="itemHeaders" :items="form.items" dense>
            <template v-slot:item.specs="{ item }">
              <span>{{ item.specs.join(' | ') }}</span>
            </template>

            <template #item.is_service="{ value }">
              <v-icon small :color="value ? 'green' : 'red'">
                {{ value ? 'mdi-check' : '' }}
              </v-icon>
            </template>

            <template v-slot:item.unit_price="{ value }">{{ currencyFormat(value) }}</template>

            <template v-slot:item.actions="{ item, index }">
              <!-- EDIT -->
              <v-btn icon small color="yellow darken-3" @click="openEditItem(item, index)" :disabled="form.isLocked">
                <v-icon>mdi-pencil</v-icon>
              </v-btn>

              <!-- DELETE -->
              <v-btn icon small color="red" @click="removeItem(item)" :disabled="form.isLocked">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
            <template #body.append>
              <tr>
                <th colspan="5" class="text-right">Sub Total</th>
                <td class="text-right">
                  {{ currencyFormat(actualSubtotal) }}
                </td>
                <td>&nbsp;</td>
              </tr>

              <tr>
                <th colspan="5" class="text-right">Discount ({{ discountPercent }})</th>
                <td class="text-right">
                  {{ currencyFormat(discount) }}
                </td>
                <td>&nbsp;</td>
              </tr>
              <tr>
                <th colspan="5" class="text-right">Total (vat ex)</th>
                <td class="text-right">
                  {{ currencyFormat(totalVatEx) }}
                </td>
                <td>&nbsp;</td>
              </tr>

              <tr>
                <th colspan="5" class="text-right">VAT ({{ getVatSymbol(form.vat_type) }})</th>
                <td class="text-right">
                  {{ currencyFormat(totalVatAmount) }}
                </td>
                <td>&nbsp;</td>
              </tr>

              <tr>
                <th colspan="5" class="text-right">
                  Total
                </th>
                <th class="text-right">
                  <strong class="text-h6">
                    {{ currencyFormat(totalAmount) }}
                  </strong>
                </th>
                <td>&nbsp;</td>
              </tr>
            </template>
            <template #item.amount="{ item }">
              {{ currencyFormat(item.unit_price * item.quantity) }}
            </template>
          </v-data-table>

          <v-btn small class="mt-2" @click="itemDialog = true">
            <v-icon left>mdi-plus</v-icon>
            Add Item
          </v-btn>

          <!-- ACTION -->
          <v-divider class="my-4" />

          <v-btn color="primary" @click="submit">Update</v-btn>

        </v-form>
      </v-card-text>
    </v-card>
    <v-dialog v-model="editItemDialog" max-width="600px">
      <v-card>
        <v-card-title>Edit Item</v-card-title>

        <v-card-text class="d-flex flex-column" style="gap: 10px">
          <v-text-field hide-details="auto" label="Description" v-model="editItemData.description" outlined dense />
          <v-text-field hide-details="auto" label="Unit Price" type="number" v-model="editItemData.unit_price" outlined
            dense />
          <v-text-field hide-details="auto" label="Quantity" type="number" v-model="editItemData.quantity" outlined
            dense />
          <v-textarea hide-details="auto" label="Specs" v-model="editItemData.specsText" outlined dense />
          <v-checkbox hide-details="auto" style="margin: 0;padding: 0;" label="Is Service"
            v-model="editItemData.is_service" />
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="editItemDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="updateItemConfirm">Update</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- ITEM DIALOG -->
    <v-dialog v-model="itemDialog" max-width="600px">
      <v-card>
        <v-card-title>Add Item</v-card-title>

        <v-card-text class="d-flex flex-column" style="gap: 10px;">
          <v-combobox no-filter clearable v-model="newItem.description" hide-details="auto" label="Description"
            :items="quotationItems.data.map(d => d.description)" outlined dense
            :search-input.sync="quotationItems.search" @change="onQuotationItemChange">
            <template v-slot:selection="{ item }">{{ item }}</template>
            <template v-slot:item="{ item }">
              <div class="d-flex flex-column">
                <h2 style="font-size: 14px;">
                  {{ item }}
                </h2>
                <p style="text-indent: 10px; font-size: 10px;">
                  {{ quotationItemsMap.get(item).specs.join(' | ') }}
                </p>
              </div>
            </template>
          </v-combobox>
          <v-text-field hide-details="auto" label="Unit Price" type="number" v-model="newItem.unit_price" outlined
            dense />
          <v-text-field hide-details="auto" label="Quantity" type="number" v-model="newItem.quantity" outlined dense />
          <v-textarea hide-details="auto" label="Specs" v-model="newItem.specsText" outlined dense />
          <v-checkbox hide-details="auto" style="margin:0; padding: 0" label="Is Service"
            v-model="newItem.is_service" />
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="itemDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="addItemConfirm">Add</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
// @ts-nocheck
import appAxios from '@/libs/app-axios'
import { computeQuotationDiscount, computeQuotationSubtotal, computeQuotationTotalAmount, computeQuotationTotalVatAmount, computeQuotationTotalVatEx, getVatSymbol } from '@/libs/quotation-computations'
import { confirmPrompt, currencyFormat, ErrorPopup, highlightText, LoadingPopup, loadPdf, SuccessPopup } from '@/libs/utils'
import { isAxiosError } from 'axios'
import _ from 'lodash'

export default {
  data() {
    return {
      vatTypes: ['VAT_INCLUSIVE', 'VAT_EXCLUSIVE', 'NON_VAT'],
      editItemDialog: false,
      editItemIndex: null,
      editItemData: {
        id: null,
        description: '',
        unit_price: 0,
        quantity: 1,
        specsText: '',
        is_service: false,
      },
      quotationItems: {
        data: [],
        search: '',
      },
      tacOptions: [],
      tacLoading: false,
      selectedTac: null,
      customers: [],
      customerSearch: null,
      form: {
        data: {
          subject: '',
          customer_id: '',
          vat_type: 'VAT_EXCLUSIVE',
          notes: '',
          project_code_id: null,
          project_code: '',
          discount_rate: 0,
          actual_date: '',
          terms_and_conditions: [],
        },
        projectCode: null,
        customer: null,
        items_sub_total: null,
        items_discount: null,
        items_total_vat_ex: null,
        items_vat_amount: null,
        items_total_amount: null,
        isLocked: false,
        items: [],
        errors: {},
      },
      itemDialog: false,
      itemHeaders: [
        { text: 'Description', value: 'description' },
        { text: 'Unit Price', value: 'unit_price', align: 'right' },
        { text: 'Quantity', value: 'quantity', align: 'center' },
        { text: 'Specs', value: 'specs' },
        { text: 'Service', value: 'is_service' },
        { text: 'Amount', value: 'amount', align: 'right', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false },
      ],
      projectCodes: [],
      projectCodeSearch: '',
      newItem: {
        description: '',
        unit_price: null,
        quantity: 1,
        specsText: '',
        is_service: false,
      },
    }
  },
  computed: {
    quotationItemsMap() {
      return new Map(this.quotationItems.data.map(d => [d.description, d]))
    },
    computedProjectCodes() {
      return [].concat(this.projectCodes, [this.form.projectCode])
    },
    subtotal() {
      return this.form.items.reduce((prev, item) => prev + (item.unit_price * item.quantity), 0)
    },
    actualSubtotal() {
      if (!this.subtotal) return 0
      return computeQuotationSubtotal(this.form.data.vat_type, this.subtotal)
    },
    discount() {
      if (!this.subtotal) return 0
      return computeQuotationDiscount(this.form.data.vat_type, this.subtotal, this.form.data.discount_rate)
    },
    totalVatEx() {
      if (!this.subtotal) return 0
      return computeQuotationTotalVatEx(this.form.data.vat_type, this.subtotal, this.form.data.discount_rate)
    },
    totalVatAmount() {
      if (!this.subtotal) return 0
      return computeQuotationTotalVatAmount(this.form.data.vat_type, this.subtotal, this.form.data.discount_rate)
    },
    totalAmount() {
      if (!this.subtotal) return 0
      return computeQuotationTotalAmount(this.form.data.vat_type, this.subtotal, this.form.data.discount_rate)
    },
    discountPercent() {
      if (!this.form.data.discount_rate) return '-'
      return (this.form.data.discount_rate * 100) + '%'
    },
    quotationId() {
      return this.$route.params.quotationId
    },
  },
  created() {
    this.fetchQuotation()
    this.fetchTacs()
    this.fetchProjectCodes()
    this.fetchCustomers()
  },
  watch: {
    customerSearch() {
      this.fetchCustomers()
    },
    itemDialog(val) {
      if (val) {
        this.fetchQuotationItems()
      }
    },
    'quotationItems.search': function () {
      this.fetchQuotationItems()
    },
    projectCodeSearch(val) {
      this.fetchProjectCodes()
    },
  },
  methods: {
    async fetchQuotation() {
      try {
        LoadingPopup.fire('Loading', 'Loading Quotation')
        const id = this.$route.params.quotationId
        const res = await appAxios().get(`/v3/quotations/${id}`)
        const q = res.data.data
        this.setQuotation(q)
        LoadingPopup.close()
      } catch (e) {
        LoadingPopup.close()
        if (e instanceof Error) ErrorPopup.fire('Error', e.message)
      }
    },

    setQuotation(quotation) {
      this.form.data = {
        subject: quotation.subject,
        customer_id: quotation.customer_id,
        vat_type: quotation.vat_type,
        notes: quotation.notes,
        project_code_id: quotation.project_code_id,
        project_code: quotation.project_code?.code || '',
        discount_rate: quotation.discount_rate,
        actual_date: quotation.actual_date,
        terms_and_conditions: Object.entries(quotation.terms_and_conditions || {}).map(([k, v]) => ({ key: k, value: v })),
      }
      this.form.customer = quotation.customer
      this.form.isLocked = quotation.is_locked
      this.form.projectCode = quotation.project_code

      this.form.items = (quotation.items || []).map(i => ({
        id: i.id, // ✅ IMPORTANT
        description: i.description,
        unit_price: i.unit_price,
        quantity: i.quantity,
        specs: i.specs || [],
        is_service: i.is_service,
      }))
    },
    addTC() {
      this.form.data.terms_and_conditions.push({ key: '', value: '' })
    },

    removeTC(i) {
      this.form.data.terms_and_conditions.splice(i, 1)
    },

    async removeItem(item) {
      try {
        LoadingPopup.fire('Delete', 'removing quotation item...')
        const id = this.$route.params.quotationId
        await appAxios().delete(
          `/v3/quotations/${id}/quotation-items/${item.id}`
        )
        this.form.items = this.form.items.filter(i => i.id !== item.id)
        LoadingPopup.close()
      } catch (e) {
        if (e instanceof Error) ErrorPopup.fire('Error', e.message)
      }
    },

    async addItemConfirm() {
      try {
        LoadingPopup.fire('Store', 'adding new item...')
        const id = this.$route.params.quotationId

        const payload = {
          description: this.newItem.description,
          unit_price: this.newItem.unit_price,
          quantity: this.newItem.quantity,
          specs: this.newItem.specsText
            ? this.newItem.specsText.split('\n').map(s => s.trim())
            : [],
          is_service: this.newItem.is_service,
        }

        const { data: resData } = await appAxios().post(
          `/v3/quotations/${id}/quotation-items`,
          payload
        )

        this.form.items.push({
          id: resData.data.id,
          description: resData.data.description,
          unit_price: resData.data.unit_price,
          quantity: resData.data.quantity,
          specs: resData.data.specs || [],
          is_service: resData.data.is_service,
        })
        this.itemDialog = false
        this.resetNewItem()
        LoadingPopup.close()
      } catch (e) {
        if (isAxiosError(e) && e.response?.status === 422) {
          ErrorPopup.fire('Error', e.response?.data?.message)
          return
        }
        if (e instanceof Error) ErrorPopup.fire('Error', e.message)
      }
    },

    buildTacObject() {
      const obj = {}
      this.form.data.terms_and_conditions.forEach(tc => {
        if (tc.key) obj[tc.key] = tc.value
      })
      return obj
    },

    fetchProjectCodes: _.debounce(async function () {
      try {
        const search = this.projectCodeSearch
        const res = await appAxios().get('/v3/project-codes', {
          params: {
            page: 1,
            limit: 1000,
            search,
          }
        })

        this.projectCodes = res.data.data
      } catch (e) {
        if (e instanceof Error) ErrorPopup.fire('Error', e.message)
      }
    }, 500),

    async submit() {
      try {
        LoadingPopup.fire('Update', 'updating quotion...')
        this.form.errors = {}
        const id = this.$route.params.quotationId

        const payload = {
          ...this.form.data,
          terms_and_conditions: this.buildTacObject(),
        }

        const { data: resData } = await appAxios().put(`/v3/quotations/${id}`, payload)
        this.setQuotation(resData.data)
        SuccessPopup.fire('Success', 'successfully updated quotation')
      } catch (e) {
        if (isAxiosError(e)) {
          if (e.response?.status === 422) {
            ErrorPopup.fire('Error', e.response?.data?.message)
            const { errors = {} } = e.response?.data
            const formErrors = {}
            for (const key in errors) formErrors[key] = errors[key]
            console.log(formErrors)
            this.form.errors = formErrors
            return
          }
          if (e.response?.status === 403) {
            ErrorPopup.fire('Error', e.response?.data)
            return
          }
        }
        if (e instanceof Error) ErrorPopup.fire('Error', e.message)
      }
    },

    openEditItem(item, index) {
      this.editItemIndex = index
      this.editItemData = {
        id: item.id,
        description: item.description,
        unit_price: item.unit_price,
        quantity: item.quantity,
        specsText: item.specs?.join('\n') || '',
        is_service: item.is_service,
      }
      this.editItemDialog = true
    },

    async updateItemConfirm() {
      try {
        LoadingPopup.fire('Update', 'updating quotation item...')
        const quotationId = this.$route.params.quotationId

        const payload = {
          description: this.editItemData.description,
          unit_price: this.editItemData.unit_price,
          quantity: this.editItemData.quantity,
          specs: this.editItemData.specsText
            ? this.editItemData.specsText.split('\n').map(s => s.trim())
            : [],
          is_service: this.editItemData.is_service,
        }

        const { data: resData } = await appAxios().put(
          `/v3/quotations/${quotationId}/quotation-items/${this.editItemData.id}`,
          payload
        )

        this.form.items = this.form.items.map(i => {
          if (resData.data.id === i.id) {
            return {
              id: resData.data.id,
              description: resData.data.description,
              unit_price: resData.data.unit_price,
              quantity: resData.data.quantity,
              specs: resData.data.specs || [],
              is_service: resData.data.is_service,
            }
          }
          return {
            id: i.id,
            description: i.description,
            unit_price: i.unit_price,
            quantity: i.quantity,
            specs: i.specs || [],
            is_service: i.is_service,
          }
        })

        this.editItemDialog = false
        LoadingPopup.close()
      } catch (e) {
        if (isAxiosError(e) && e.response?.status === 422) {
          ErrorPopup.fire('Error', e.response?.data?.message)
          return
        }
        if (e instanceof Error) ErrorPopup.fire('Error', e.message)
      }
    },
    async fetchTacs() {
      this.tacLoading = true
      try {
        const res = await appAxios().get('/v3/quotation-tacs')
        this.tacOptions = res.data
      } finally {
        this.tacLoading = false
      }
    },

    applyTac(tac) {
      if (!tac || !tac.properties) return

      this.form.data.terms_and_conditions = Object.entries(tac.properties).map(
        ([key, value]) => ({
          key,
          value,
        })
      )
    },

    async saveTac() {
      try {
        const alias = prompt('Enter preset name (alias):')
        if (!alias) return

        const payload = {
          alias,
          properties: this.buildTacObject(),
        }

        const res = await appAxios().post('/v3/quotation-tacs', payload)
        SuccessPopup.fire('Success', res.data)

        await this.fetchTacs()
        this.selectedTac = this.tacOptions.find(opt => opt.alias === alias.trim())
      } catch (e) {
        if (e instanceof Error) ErrorPopup.fire('Error', e.message)
      }
    },

    async updateTac() {
      try {
        if (!this.selectedTac?.id) return

        const payload = {
          alias: this.selectedTac.alias,
          properties: this.buildTacObject(),
        }

        const res = await appAxios().put(
          `/v3/quotation-tacs/${this.selectedTac.id}`,
          payload
        )

        SuccessPopup.fire('Success', res.data)
      } catch (e) {
        if (e instanceof Error) {
          ErrorPopup.fire('Error', e.message)
        }
      }
    },
    fetchQuotationItems: _.debounce(async function () {
      try {
        const res = await appAxios().get('/v3/quotation-items', {
          params: {
            page: 1,
            limit: 1000,
            search: this.quotationItems.search,
            unique: true,
          },
        })
        this.quotationItems.data = res.data.data
      } catch (e) {
        if (e instanceof Error) ErrorPopup.fire('Error', e.message)
      }
    }, 500),
    resetNewItem() {
      this.newItem = {
        description: '',
        unit_price: null,
        quantity: 1,
        specsText: '',
        is_service: false,
      }
    },
    onQuotationItemChange(description) {
      const item = this.quotationItemsMap.get(description)
      if (!item) return

      this.newItem.specsText = item.specs?.join('\n') || ''
      this.newItem.unit_price = item.unit_price || 0
      this.newitem.is_service = item.is_service
    },
    async confirmLock() {
      const confirmed = confirmPrompt('LOCK QUOTATION', 'Type "LOCK QUOTATION" to confirm locking this quotation.')
      if (!confirmed) return
      this.lockQuotation()
    },
    async lockQuotation() {
      try {
        LoadingPopup.fire('Locking', 'Locking quotation...')

        const id = this.$route.params.quotationId

        const res = await appAxios().post(`/v3/quotations/${id}/lock`)

        this.form.isLocked = true

        LoadingPopup.close()
        SuccessPopup.fire('Success', res.data)
      } catch (e) {
        LoadingPopup.close()

        if (isAxiosError(e)) {
          ErrorPopup.fire('Error', e.response?.data || 'Failed to lock quotation')
          return
        }

        if (e instanceof Error) {
          ErrorPopup.fire('Error', e.message)
        }
      }
    },
    async confirmUnlock() {
      const confirm = window.confirm('Unlock this quotation?')

      if (!confirm) return

      try {
        LoadingPopup.fire('Unlocking', 'Unlocking quotation...')

        const id = this.$route.params.quotationId
        const res = await appAxios().post(`/v3/quotations/${id}/unlock`)

        this.form.isLocked = false

        LoadingPopup.close()
        SuccessPopup.fire('Success', res.data)
      } catch (e) {
        LoadingPopup.close()

        if (isAxiosError(e)) {
          ErrorPopup.fire('Error', e.response?.data || 'Failed to unlock')
          return
        }

        if (e instanceof Error) {
          ErrorPopup.fire('Error', e.message)
        }
      }
    },

    async printPdf() {
      try {
        LoadingPopup.fire('Loading', 'Generating PDF...')
        const { data } = await appAxios().get(`/v3/quotations/${this.quotationId}/pdf`, {
          responseType: 'blob',
        })
        const blob = new Blob([data], { type: 'application/pdf' })
        loadPdf(blob)
        LoadingPopup.close()
      } catch (e) {
        if (isAxiosError(e)) {
          ErrorPopup.fire('Error', e.response?.data?.message || 'Failed to load PDF')
          return
        }
        if (e instanceof Error) {
          ErrorPopup.fire('Error', e.message)
        }
      }
    },
    fetchCustomers: _.debounce(async function () {
      try {
        const res = await appAxios().get('/v3/customers', {
          params: {
            page: 1,
            limit: 1000,
            search: this.customerSearch,
          },
        })
        this.customers = res.data.data.concat(this.form.customer)
      } catch (e) {
        if (isAxiosError(e)) {
          ErrorPopup.fire('Error', e.response?.data?.message || 'Failed to fetch customers')
          return
        }
        if (e instanceof Error) ErrorPopup.fire('Error', e.message)
      }
    }, 500),
    highlightText,
    getVatSymbol,
    currencyFormat,
  },
}
</script>
